/**
 * OCR Connector configuration component.
 * Specialized configuration for OCR connector (Google Cloud Vision / Azure Document Intelligence).
 */

// React Imports
import { useState } from 'react';
import { Button, Input } from '@coordio/ui';
import { CloudUpload as UploadIcon } from 'lucide-react';

export interface OcrConnectorConfigProps {
  settingsJson?: string;
  onChange?: (settingsJson: string) => void;
}

/**
 * OCR Connector configuration component.
 */
export const OcrConnectorConfig = ({
  settingsJson = '{}',
  onChange,
}: OcrConnectorConfigProps) => {
  const [provider, setProvider] = useState<'google' | 'azure'>('google');
  const [apiKey, setApiKey] = useState('');
  const [projectId, setProjectId] = useState('');
  const [defaultLanguage, setDefaultLanguage] = useState('en');
  const [baseUrl, setBaseUrl] = useState('');
  const [defaultModel, setDefaultModel] = useState('');

  const handleProviderChange = (newProvider: 'google' | 'azure') => {
    setProvider(newProvider);
    const settings = JSON.parse(settingsJson || '{}');
    settings.provider = newProvider;
    onChange?.(JSON.stringify(settings));
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && provider === 'google') {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        const settings = JSON.parse(settingsJson || '{}');
        settings.serviceAccountJson = content;
        onChange?.(JSON.stringify(settings));
      };
      reader.readAsText(file);
    }
  };

  return (
    <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
      <div className="p-6">
        <h6 className="text-lg font-semibold tracking-tight mb-6">
          OCR Provider Configuration
        </h6>

        <div className="mb-6">
          <p className="text-sm text-muted-foreground mb-2">
            Select Provider
          </p>
          <div className="inline-flex w-full rounded-md border" role="group">
            <button
              type="button"
              className={`flex-1 px-4 py-2 text-sm font-medium border-r transition-colors rounded-l-md ${
                provider === 'google' ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted'
              }`}
              onClick={() => handleProviderChange('google')}
            >
              Google Cloud Vision
            </button>
            <button
              type="button"
              className={`flex-1 px-4 py-2 text-sm font-medium transition-colors rounded-r-md ${
                provider === 'azure' ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted'
              }`}
              onClick={() => handleProviderChange('azure')}
            >
              Azure Document Intelligence
            </button>
          </div>
        </div>

        {provider === 'google' && (
          <div className="flex flex-col gap-4">
            <div>
              <p className="text-sm text-muted-foreground mb-2">
                Service Account JSON Key
              </p>
              <Button variant="outline" className="w-full" asChild>
                <label className="cursor-pointer">
                  <UploadIcon size={16} className="mr-2" />
                  Upload Service Account JSON
                  <input
                    type="file"
                    className="hidden"
                    accept=".json"
                    onChange={handleFileUpload}
                  />
                </label>
              </Button>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">API Key (Alternative)</label>
              <Input
                type="password"
                className="w-full"
                value={apiKey}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setApiKey(e.target.value);
                  const settings = JSON.parse(settingsJson || '{}');
                  settings.apiKey = e.target.value;
                  onChange?.(JSON.stringify(settings));
                }}
              />
              <p className="text-xs text-muted-foreground">Alternative: Use API key instead of service account</p>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Project ID</label>
              <Input
                type="text"
                className="w-full"
                value={projectId}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setProjectId(e.target.value);
                  const settings = JSON.parse(settingsJson || '{}');
                  settings.projectId = e.target.value;
                  onChange?.(JSON.stringify(settings));
                }}
              />
              <p className="text-xs text-muted-foreground">Google Cloud project ID</p>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Default Language</label>
              <Input
                type="text"
                className="w-full"
                value={defaultLanguage}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setDefaultLanguage(e.target.value);
                  const settings = JSON.parse(settingsJson || '{}');
                  settings.defaultLanguage = e.target.value;
                  onChange?.(JSON.stringify(settings));
                }}
              />
              <p className="text-xs text-muted-foreground">Default language code (e.g., en, es, fr)</p>
            </div>
          </div>
        )}

        {provider === 'azure' && (
          <div className="flex flex-col gap-4">
            <div className="space-y-1">
              <label className="text-sm font-medium">API Endpoint</label>
              <Input
                type="text"
                className="w-full"
                value={baseUrl}
                onChange={(e) => {
                  setBaseUrl(e.target.value);
                  const settings = JSON.parse(settingsJson || '{}');
                  settings.endpoint = e.target.value;
                  onChange?.(JSON.stringify(settings));
                }}
              />
              <p className="text-xs text-muted-foreground">Azure Document Intelligence endpoint URL</p>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">API Key</label>
              <Input
                type="password"
                className="w-full"
                value={apiKey}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setApiKey(e.target.value);
                  const settings = JSON.parse(settingsJson || '{}');
                  settings.apiKey = e.target.value;
                  onChange?.(JSON.stringify(settings));
                }}
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Default Model ID</label>
              <Input
                type="text"
                className="w-full"
                value={defaultModel}
                onChange={(e) => {
                  setDefaultModel(e.target.value);
                  const settings = JSON.parse(settingsJson || '{}');
                  settings.defaultModelId = e.target.value;
                  onChange?.(JSON.stringify(settings));
                }}
              />
              <p className="text-xs text-muted-foreground">Pre-built model ID (e.g., prebuilt-document, prebuilt-invoice)</p>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Default Language</label>
              <Input
                type="text"
                className="w-full"
                value={defaultLanguage}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setDefaultLanguage(e.target.value);
                  const settings = JSON.parse(settingsJson || '{}');
                  settings.defaultLanguage = e.target.value;
                  onChange?.(JSON.stringify(settings));
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
